import { alpha, IconButton, InputAdornment, styled, TextFieldProps } from '@mui/material';
import { Eye } from 'lucide-react';
import React, { ElementType, useState } from 'react';
// Sibling slice by relative path, not the `@shared/ui` barrel: importing the barrel from inside it
// is a cycle. Same shape as `document-option` → `../file-extension`.
import { RedditTextField } from '../reddit-text-field';

const PasswordToggleButton = styled(IconButton)(({ theme }) => ({
  color: alpha(theme.palette.primary.darker, 0.6),
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

export type PasswordFieldProps = Omit<TextFieldProps, 'type'> & {
  /** Underlying text field to render. Defaults to the app's filled `RedditTextField`. */
  TextFieldComponent?: ElementType;
  /** Size (px) passed to the lucide eye icon. */
  iconSize?: number;
  /** Accessible label for the show/hide toggle button. */
  toggleAriaLabel?: string;
};

/**
 * Password input with a self-contained show/hide toggle.
 *
 * Manages its own visibility state and injects the toggle button as an end
 * adornment, preserving any `slotProps` the caller provides. Works with
 * react-hook-form `Controller` by forwarding field props and the ref.
 *
 * THE EYE IS NOT A TAB STOP (#633): Tab goes from field to field, the usual
 * convention for a reveal toggle. It stays a named button in the accessibility tree —
 * clickable, and reachable through a screen reader's own navigation — but a
 * keyboard-only user can no longer reveal the password; the field itself is unaffected.
 */
const PasswordField = React.forwardRef<HTMLDivElement, PasswordFieldProps>(function PasswordField(
  {
    TextFieldComponent = RedditTextField,
    iconSize,
    toggleAriaLabel = 'toggle password visibility',
    slotProps,
    ...rest
  },
  ref,
) {
  const [showPassword, setShowPassword] = useState(false);
  const Base = TextFieldComponent;

  return (
    <Base
      {...rest}
      ref={ref}
      type={showPassword ? 'text' : 'password'}
      slotProps={{
        ...slotProps,
        input: {
          ...(slotProps?.input as object),
          endAdornment: (
            <InputAdornment position='end'>
              <PasswordToggleButton
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={toggleAriaLabel}
                tabIndex={-1}
              >
                <Eye size={iconSize} />
              </PasswordToggleButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
});

export default PasswordField;
